'use-client';
import * as React from 'react';
import {
  formatClassNames,
  sanitizeHTML,
  getDataAttributes,
  getTabIndexAttribute,
} from '@wix/editor-elements-common-utils';
import { useResizeObserver } from '@wix/thunderbolt-elements/src/providers/useResizeObserver';
import type { ICollapsibleTextCompProps } from '../CollapsibleText.types';
import { ExpandModeValues, TestIds } from '../constants';
import { TagValue } from '../../../common/panel-sections/seoAndA11y/consts';
import semanticClassNames from '../CollapsibleText.semanticClassNames';
import { st, classes } from './style/CollapsibleText.component.st.css';
import { ExpandControl } from './ExpandControl/ExpandControl';
import { isSafariVersionLess } from './utils/isSafariVersionLess';

const noop = () => {};

/* line clamp doens't work properly with block items in Safari version less 17 */
const safariGoodVersion = 17;

const isRichText = (text: string) => /<\/?[a-z][\s\S]*>/i.test(text);

const getIsExpandRedundant = (
  textRef: React.RefObject<HTMLDivElement>,
  maxLines: number,
) => {
  const txtElement = textRef.current;
  if (txtElement) {
    const computedStyle = getComputedStyle(txtElement);
    const lineHeight = computedStyle.lineHeight;
    let lineHeightAsNumber = parseFloat(lineHeight);

    // Calculating based on the font when the line-height isn't a number (f.e when it's "normal"/"unset")
    if (!Number.isInteger(lineHeight)) {
      const fontSize = parseFloat(computedStyle.fontSize);
      const fallbackRatio = 1.5;
      lineHeightAsNumber = fontSize * fallbackRatio;
    }

    const maxHeight = Math.ceil(lineHeightAsNumber * maxLines);
    return txtElement.scrollHeight <= maxHeight;
  }

  return false;
};

const CollapsibleText: React.FC<ICollapsibleTextCompProps> = props => {
  const {
    id,
    text,
    className,
    customClassNames = [],
    stylableClassName,
    direction,
    showExpandControl,
    expandMode,
    ellipsis,
    wrappingStyle,
    isExpanded,
    maxLines,
    tag: TagName = TagValue.P,
    onClick = noop,
    onDblClick = noop,
    onMouseEnter = noop,
    onMouseLeave = noop,
    lang,
  } = props;
  const textRef = React.useRef<HTMLDivElement>(null);
  const [isExpandRedundant, setIsExpandRedundant] = React.useState(false);
  const [shouldShowExpandControl, setShouldShowExpandControl] =
    React.useState(false);

  const textFormat = React.useMemo(() => {
    return isRichText(text) ? 'rtf' : 'plain';
  }, [text]);

  useResizeObserver({
    ref: textRef,
    callback: () =>
      setIsExpandRedundant(getIsExpandRedundant(textRef, maxLines)),
  });

  React.useEffect(() => {
    setIsExpandRedundant(getIsExpandRedundant(textRef, maxLines));
  }, [text, maxLines]);

  React.useEffect(() => {
    const shouldShow =
      ellipsis &&
      ((expandMode === ExpandModeValues.Link && showExpandControl) ||
        (expandMode === ExpandModeValues.Expand &&
          (isExpanded || !isExpandRedundant)));

    setShouldShowExpandControl(shouldShow);
  }, [ellipsis, expandMode, showExpandControl, isExpanded, isExpandRedundant]);

  const textClassName = st(
    classes.text,
    {
      collapse: ellipsis && !isExpanded,
      wrappingStyle,
      textFormat,
      withSafariListsFix:
        wrappingStyle === 'multi-line' &&
        isSafariVersionLess(safariGoodVersion),
    },
    formatClassNames(semanticClassNames.text),
  );

  // Needed for rich-text support
  const Text: React.FC<{ _text: string }> = ({ _text }) =>
    React.createElement(TagName, {
      dangerouslySetInnerHTML: {
        __html: sanitizeHTML(_text.trim()),
      },
      className: textClassName,
      ref: textRef,
    });

  return (
    <div
      id={id}
      {...getDataAttributes(props)}
      {...getTabIndexAttribute(props.a11y)}
      className={className}
      onClick={onClick}
      onDoubleClick={onDblClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      lang={lang}
    >
      <div
        dir={direction}
        className={st(
          classes.root,
          {},
          stylableClassName,
          formatClassNames(semanticClassNames.root, ...customClassNames),
        )}
        data-testid={TestIds.viewer.root}
      >
        {shouldShowExpandControl && <ExpandControl {...props} />}
        <div
          data-testid={TestIds.viewer.text_wrapper}
          className={st(classes['text-wrapper'])}
        >
          <Text _text={text} />
        </div>
      </div>
    </div>
  );
};

export default CollapsibleText;
